import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { GiraffePeek } from "@/components/GiraffePeek";
import { GiraffePopout } from "@/components/GiraffePopout";
import { PresenceTracker } from "@/components/PresenceTracker";
import Home from "@/pages/Home";
import Article from "@/pages/Article";
import About from "@/pages/About";
import Sponsor from "@/pages/Sponsor";
import GiraffeMap from "@/pages/GiraffeMap";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Dashboard from "@/pages/admin/Dashboard";
import Editor from "@/pages/admin/Editor";
import AboutEditor from "@/pages/admin/AboutEditor";
import Sponsors from "@/pages/admin/Sponsors";
import Messages from "@/pages/admin/Messages";
import Categories from "@/pages/admin/Categories";
import Recommendations from "@/pages/Recommendations";
import RecommendationDetail from "@/pages/RecommendationDetail";
import SubcategoryProducts from "@/pages/SubcategoryProducts";
import RecommendationsAdmin from "@/pages/admin/RecommendationsAdmin";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import ThankYou from "@/pages/ThankYou";
import IlCassetto from "@/pages/IlCassetto";
import CategoryHub from "@/pages/CategoryHub";
import CassettoAdmin from "@/pages/admin/CassettoAdmin";
import LostGiraffeAdmin from "@/pages/admin/LostGiraffeAdmin";
import { Navigate } from "react-router-dom";
import { applySeo } from "@/lib/seo";

const ROUTE_META = {
  "/": {
    title: "Il Cassetto delle Giraffe — di Fabio Campana",
    description: "Storie, idee e curiosità per guardare il mondo con occhi diversi. Il blog personale di Fabio Campana.",
  },
  "/giraffe-map": {
    title: "Giraffe Map — Il Cassetto delle Giraffe",
    description: "Esplora tutte le categorie del blog: cinema, arte, scienza, filosofia e molto altro.",
  },
  "/il-cassetto": {
    title: "Il Cassetto — Il Cassetto delle Giraffe",
    description: "La giraffa ha una domanda per te. Rispondi in modo anonimo e leggi le risposte degli altri lettori.",
  },
  "/le-giraffe-consigliano": {
    title: "Le Giraffe Consigliano — Il Cassetto delle Giraffe",
    description: "Prodotti scelti e recensiti con cura, organizzati per categoria.",
  },
  "/chi-sono": {
    title: "Chi sono — Il Cassetto delle Giraffe",
    description: "Fabio Campana: chi sono e perché ho creato Il Cassetto delle Giraffe.",
  },
  "/sponsor": {
    title: "Sponsor — Il Cassetto delle Giraffe",
    description: "Porta la tua azienda su Il Cassetto delle Giraffe.",
  },
  "/contatti": {
    title: "Contatti — Il Cassetto delle Giraffe",
    description: "Scrivimi: proposte, collaborazioni e sponsorizzazioni.",
  },
  "/privacy": {
    title: "Privacy — Il Cassetto delle Giraffe",
    description: "Informativa sulla privacy di Il Cassetto delle Giraffe.",
  },
  "/termini": {
    title: "Termini — Il Cassetto delle Giraffe",
    description: "Termini e condizioni di Il Cassetto delle Giraffe.",
  },
};

// Global, deterministic SEO for every route: self-referential canonical (no www),
// per-route title/description, and noindex for private admin pages.
// Article and product pages enrich these tags further after their data loads.
const RouteSeo = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const noindex = pathname.startsWith("/admin") || pathname === "/accedi";
    const meta = ROUTE_META[pathname];
    applySeo({
      path: pathname,
      title: meta?.title,
      description: meta?.description,
      noindex,
    });
  }, [pathname]);
  return null;
};

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center font-ui text-sm text-[#57534E]">
        Caricamento…
      </div>
    );
  if (!user) return <Navigate to="/accedi" replace />;
  return children;
};

function App() {
  return (
    <div className="App grain">
      <AuthProvider>
        <BrowserRouter>
          <RouteSeo />
          <PresenceTracker />
          <WhatsAppButton />
          <GiraffePeek />
          <GiraffePopout />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/giraffe-map" element={<GiraffeMap />} />
            <Route path="/categoria/:slug" element={<CategoryHub />} />
            <Route path="/il-cassetto" element={<IlCassetto />} />
            <Route path="/le-giraffe-consigliano" element={<Recommendations />} />
            <Route path="/le-giraffe-consigliano/sottocategoria/:subId" element={<SubcategoryProducts />} />
            <Route path="/le-giraffe-consigliano/:id" element={<RecommendationDetail />} />
            <Route path="/articolo/:slug" element={<Article />} />
            <Route path="/chi-sono" element={<About />} />
            <Route path="/sponsor" element={<Sponsor />} />
            <Route path="/contatti" element={<Contact />} />
            <Route path="/termini" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/grazie" element={<ThankYou />} />
            <Route path="/accedi" element={<Login />} />
            <Route path="/admin" element={<Protected><Dashboard /></Protected>} />
            <Route path="/admin/scrivi" element={<Protected><Editor /></Protected>} />
            <Route path="/admin/scrivi/:id" element={<Protected><Editor /></Protected>} />
            <Route path="/admin/chi-sono" element={<Protected><AboutEditor /></Protected>} />
            <Route path="/admin/sponsor" element={<Protected><Sponsors /></Protected>} />
            <Route path="/admin/messaggi" element={<Protected><Messages /></Protected>} />
            <Route path="/admin/categorie" element={<Protected><Categories /></Protected>} />
            <Route path="/admin/consigli" element={<Protected><RecommendationsAdmin /></Protected>} />
            <Route path="/admin/cassetto" element={<Protected><CassettoAdmin /></Protected>} />
            <Route path="/admin/giraffa-perduta" element={<Protected><LostGiraffeAdmin /></Protected>} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" />
      </AuthProvider>
    </div>
  );
}

export default App;
